import React, { useEffect } from 'react';
import { useTable, useSortBy, useExpanded } from 'react-table';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    IconButton,
    Grid,
    Card,
} from '@mui/material';
import DialogTitle from '@mui/material/DialogTitle';

// MUI Icons
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import CircularProgress from '@mui/material/CircularProgress';

// State management; see DataContext.js
import { useDataSetters, useDataState } from '../../../DataContext';

// CSS
import './LandingDashboard.css';

const headerColors = {
    'expander': '#ffffff',  // Color for expander column
    'id': '#ffffff',        // Color for ID column
    'total_vines': '#616161', // Color for Total Vines column
    'Strong': '#3A9D44', // Color for Mature Vines column,
    'Weak': '#e6ae1e', // Color for Weak Vines column,
    'Young': '#8ABB3D', // Color for Young Vines column,
    'Replant': '#bfae6b', // Color for Replants column,
    'Rootstock': '#8c6c30', // Color for Rootstocks column,
    'No Vine': '#b5b5b5', // Color for Missing Vines column,
    'No Data': '#000000', // Color for No Data column,
};

const PlantAssetInventoryTreeTable = ({ data }) => {

    // State management; see DataContext.js
    const {
        plantAssetClasses,
        expandedRows,
    } = useDataState();

    const {
        setClickedDashboardTableCell,
        setPlantAssetTable,
        setExpandedRows,
    } = useDataSetters();

    const isLoading = plantAssetClasses ? plantAssetClasses.length === 0 : false;

    // Handle table cell click
    const handleCellClick = (cell) => {
        setClickedDashboardTableCell(cell);
    };

    const sumMetricForBlocks = (blocks, metric) => {
        console.log("Blocks for metric ", metric, ": ", blocks);
        console.log('blocks here ', blocks)
        
        let sum = blocks.reduce((acc, block) => {
            console.log("Current Block: ", block);
            return acc + (Number(block[metric]) || 0);
        }, 0);
        
        console.log("Sum for metric ", metric, ": ", sum);
        
        let totalVines = blocks.reduce((acc, block) => {
            return acc + Object.values(block).reduce((innerAcc, value) => {
                return innerAcc + (typeof value === 'number' ? value : 0);
            }, 0);
        }, 0);
        
        console.log("Total vines for metric ", metric, ": ", totalVines);
        
        return { sum, totalVines };
    };
    

    const renderCellWithPercentage = (cell) => {
        const totalVines = cell.row.values.total_vines;
        if (totalVines && totalVines > 0) {
            const value = cell.value;  // This now gets the raw number for the vine class
            const percentage = ((value / totalVines) * 100).toFixed(0);
            return `${percentage}% (${value})`;
        }
        return cell.value;  // default case when total_vines is not available or zero
    };     

    const numberAccessor = (metric) => (row) => {
        return row[metric] || 0;
    };

    const percentageSortType = (rowA, rowB, columnId) => {
        const a = (rowA.values[columnId] / rowA.values.total_vines) || 0;
        const b = (rowB.values[columnId] / rowB.values.total_vines) || 0;
        return a > b ? 1 : -1;
    };
    

    const columns = React.useMemo(
        () => [
            {
                id: 'expander',
                Cell: ({ row }) => (
                    // Hide expander icon for depth of 2 (third level)
                    row.depth < 2 ? (
                        <IconButton 
                            style={{ marginLeft: `${row.depth * 15}px` }} 
                            onClick={(e) => {
                                // Use the toggle provided by react-table
                                row.toggleRowExpanded();
                                e.stopPropagation();
                            }}
                        >
                            {row.isExpanded ? <ExpandMoreIcon /> : <ChevronRightIcon />}
                        </IconButton>
                    ) : null // render nothing for depth of 2
                ),
            },
            { 
                Header: 'ID', 
                accessor: 'id'
            },
            { 
                Header: 'Spaces', 
                accessor: 'total_vines',
            },
            { 
                Header: 'Strong', 
                accessor: numberAccessor('number_mature_vines'),
                Cell: (cellProps) => renderCellWithPercentage(cellProps.cell) ,
                sortType: percentageSortType,
            },          
            { 
                Header: 'Weak', 
                accessor: numberAccessor('number_weak_vines'),
                Cell: (cellProps) => renderCellWithPercentage(cellProps.cell) ,
                sortType: percentageSortType,
            },       
            { 
                Header: 'Young', 
                accessor: numberAccessor('number_young_vines'),
                Cell: (cellProps) => renderCellWithPercentage(cellProps.cell),
                sortType: percentageSortType,
            },
            { 
                Header: 'Replant', 
                accessor: numberAccessor('number_replanted_vines'),
                Cell: (cellProps) => renderCellWithPercentage(cellProps.cell),
                sortType: percentageSortType,
            },
            { 
                Header: 'Rootstock', 
                accessor: numberAccessor('number_rootstocks'),
                Cell: (cellProps) => renderCellWithPercentage(cellProps.cell),
                sortType: percentageSortType,
            },
            { 
                Header: 'No Vine', 
                accessor: numberAccessor('number_missing_vines'),
                Cell: (cellProps) => renderCellWithPercentage(cellProps.cell),
                sortType: percentageSortType,
            },
            { 
                Header: 'No Data', 
                accessor: numberAccessor('number_nodata_vines'),
                Cell: (cellProps) => renderCellWithPercentage(cellProps.cell),
                sortType: percentageSortType,
            },
        ],
        []
    );

    const transformedData = React.useMemo(() => {
        const allMetrics = [
            'number_missing_vines', 
            'number_rootstocks', 
            'number_replanted_vines', 
            'number_young_vines', 
            'number_mature_vines',
            'number_nodata_vines',
            'number_weak_vines',
            'number_weak_canopies',
            'number_weak_fruits'
        ];
        const allVineyardsSum = {};  // Initialize to empty object
    
        const calculateTotalVinesForRow = (row) => {
            const excludedMetrics = ["number_weak_canopies", "number_weak_fruits"];
            return allMetrics.reduce((acc, metric) => {
                if (!excludedMetrics.includes(metric)) {
                    return acc + (row[metric] || 0);
                }
                return acc;
            }, 0);
        };
    
        data = data || [];
        const vineyardData = data.map(vineyard => {
            const summedMetrics = {};
            let vineyardTotalVines = 0;  // Initialize to 0 for this vineyard
    
            allMetrics.forEach(metric => {
                const { sum } = sumMetricForBlocks(vineyard.blocks, metric);
                summedMetrics[metric] = sum;
                vineyardTotalVines += sum;  // Increment vineyard total with sum of this metric
    
                if (allVineyardsSum[metric]) {
                    allVineyardsSum[metric] += sum; // Increment overall total with sum of this metric
                } else {
                    allVineyardsSum[metric] = sum; // Initialize overall total with sum of this metric
                }
            });
    
            summedMetrics['total_vines'] = vineyardTotalVines;
    
            const blockData = vineyard.blocks.map(block => {
                const totalVinesForBlock = calculateTotalVinesForRow(block);
                return {
                    id: block.block_id,
                    total_vines: totalVinesForBlock,
                    ...block,
                };
            });
    
            return {
                id: vineyard.vineyard_id,
                total_vines: vineyardTotalVines,
                ...summedMetrics,
                subRows: blockData,
            };
        });
    
        allVineyardsSum['total_vines'] = allMetrics.reduce((acc, metric) => acc + (allVineyardsSum[metric] || 0), 0);  // Calculate 'total_vines' for 'All Vineyards'
    
        return [
            {
                id: 'All Vineyards',
                ...allVineyardsSum, 
                subRows: vineyardData,
            },
        ];
    }, [data]);

    const hasData = transformedData.length && transformedData[0].subRows && transformedData[0].subRows.length;
      
    const expandedState = React.useMemo(() => {
        let obj = {};
        expandedRows.forEach(rowId => {
            obj[rowId] = true;
        });
        return obj;
    }, [expandedRows]);
    
    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        rows,
        prepareRow,
        state: { expanded }
    } = useTable(
        {
            columns,
            data: transformedData,
            initialState: { sortBy: [{ id: 'ID', desc: true }], expanded: expandedState }
        },
        useSortBy,
        useExpanded,
    );

    // Listen for changes in the `expanded` state
    useEffect(() => {
        const expandedRowIds = Object.keys(expanded).filter(rowId => expanded[rowId]);
        setExpandedRows(expandedRowIds);
    }, [expanded, setExpandedRows]);

    useEffect(() => {
        setPlantAssetTable(transformedData);
        console.log('transformedData', transformedData)
    }, [transformedData]);

    console.log('plantAssetClasses', plantAssetClasses);

    return (
        <Grid container spacing={1} style={{ marginRight: '95px', marginLeft: '20px', marginTop: '5px'}} component={Paper}>
            <Grid item xs={12}> 
                <DialogTitle id="settings-dialog-title">Vine Asset Inventory</DialogTitle>
                <Card elevation={2} style={{ borderRadius: '5px', margin: '20px 20px 20px 20px' }}>
                <TableContainer component={Paper}>
                    <Table {...getTableProps()}>
                    <TableHead>
                        {headerGroups.map(headerGroup => (
                            <TableRow {...headerGroup.getHeaderGroupProps()}>
                                {headerGroup.headers.map(column => (
                                    <TableCell  
                                    {...(column.id === 'expander' ? column.getHeaderProps() : column.getHeaderProps(column.getSortByToggleProps()))} 
                                        style={{ 
                                            fontWeight: 'bold', 
                                            color: 'white',
                                            backgroundColor: headerColors[column.id] || '#3874cb',  // Use the default color if no specific color is defined
                                            borderTopLeftRadius: '10px',  // Only apply left-radius to the first cell
                                            borderTopRightRadius: '10px',  // Only apply right-radius to the last cell
                                            border: 'none',  // Remove borders
                                            boxShadow: 'none',  // Remove shadows
                                        }}
                                    >
                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
                                            {column.render('Header')}
                                            {column.id !== 'expander' && (
                                                <div style={{ display: 'flex', flexDirection: 'column', marginLeft: '8px' }}>
                                                    <ArrowDropUpIcon 
                                                        fontSize="medium" 
                                                        style={{ marginBottom: '-6px', opacity: column.isSorted ? (column.isSortedDesc ? 0.5 : 1) : 0.5 }} 
                                                    />
                                                    <ArrowDropDownIcon 
                                                        fontSize="medium" 
                                                        style={{ marginTop: '-6px', opacity: column.isSorted ? (column.isSortedDesc ? 1 : 0.5) : 0.5 }} 
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    </TableCell>
                                ))}
                            </TableRow>
                        ))}
                    </TableHead>

                    <TableBody {...getTableBodyProps()}>
                        {hasData ? (
                            rows.map(row => {
                                prepareRow(row);
                                const isBold = row.depth === 0; // Top-level should be bold
                                const bgColor = row.depth === 0 ? '#f3f3f3' : row.depth === 1 ? '#f9f9f9' : '#ffffff';
                                return (
                                    <TableRow {...row.getRowProps()} style={{ backgroundColor: bgColor }} className="hoverable-row">
                                        {row.cells.map(cell => {
                                            const isExpander = cell.column.id === 'expander';
                                            const isIdColumn = cell.column.id === 'id';
                                            const paddingLeft = isExpander ? `${row.depth * 1}px` : null;
    
                                            // Check if it's either the expander or id column
                                            if (isExpander || isIdColumn) {
                                                return (
                                                    <TableCell 
                                                        {...cell.getCellProps()} 
                                                        style={{ 
                                                            paddingLeft, 
                                                            textAlign: 'center', 
                                                            cursor: 'pointer'
                                                        }}
                                                        onClick={() => handleCellClick(cell)}
                                                    >
                                                        {cell.render('Cell')}
                                                    </TableCell>
                                                );
                                            }
                                            
                                            return (
                                                <TableCell 
                                                    {...cell.getCellProps()} 
                                                    style={{ 
                                                        paddingLeft, 
                                                        textAlign: 'center', 
                                                        cursor: 'pointer'
                                                    }}
                                                    onClick={() => handleCellClick(cell)}
                                                >
                                                    {isLoading ? <CircularProgress size={20} /> : cell.render('Cell')}
                                                </TableCell>
                                            );
                                        })}
                                    </TableRow>
                                );
                            })
                        ) : (
                            <TableRow>
                                <TableCell colSpan={columns.length} style={{ textAlign: 'center', fontWeight: 'bold' }}>
                                    {isLoading ? <CircularProgress size={20} /> : "No Asset Class Data"}
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>

                    </Table>
                </TableContainer>
                </Card>
            </Grid>
        </Grid>
    );
};

export default PlantAssetInventoryTreeTable;